import React from 'react';
import classNames from 'classnames';
import propTypes from 'prop-types';
import { getMessage } from 'util/locale';
import { connect } from 'react-redux';

// selectors
import {
  getActiveFieldId,
  getSelectedOverlayLayerGroup,
  getSelectedOverlayLayer,
  getSelectedOverlaySeason,
  getSelectedOverlayUuid,
  showsOverlayDates,
  showsOverlaySeasons
} from 'current/selectors';
import {
  getImageSeasonsOfLayerForField,
  getImagesOfLayerForField,
  getUsableOverlayLayerGroups
} from 'imagery/selectors';

// actions
import {
  setIsOverlaySelectModalActive,
  setSelectedOverlayUuid,
  setSelectedOverlayLayerGroup,
  setSelectedOverlayLayerWithDefaults,
  setSelectedOverlaySeasonWithDefaults
} from 'current/uiwidgets';

import {
  setActiveOverlayInfo
} from 'current';

// Components
import { Button, Link, Modal } from '@tcc/crate';
import OverlaySelectLayerGroups from 'imagery/OverlaySelectLayerGroups';
import OverlaySelectLayers from 'imagery/OverlaySelectLayers';
import OverlaySelectLayerSeasons from 'imagery/OverlaySelectLayerSeasons';
import OverlaySelectLayerDates from 'imagery/OverlaySelectLayerDates';
import { FIELD_VIEW_URL } from 'constants/urls';

let OverlaySelectModalContainer = ({
  layerGroups,
  layerImages,
  layerSeasons,
  onCancelModal,
  onSaveModal,
  onSelectLayer,
  onSelectLayerGroup,
  onSelectSeason,
  onSelectUuid,
  selectedLayer,
  selectedLayerGroup,
  selectedSeason,
  selectedUuid,
  showsDates,
  showsSeasons
}) => {
  const baseString = 'vra-layer-selector-modal';
  const uploadTrigger = (
    <Link
      className='vra-layer-selector__button--upload-trigger'
      data-test-id={`${baseString}-upload-trigger`}
      href={FIELD_VIEW_URL.DATA_INBOX}>
      {getMessage('button-text__upload-more-maps')}
    </Link>
  );

  var classString = classNames({
    [`${baseString}`]: true
  });

  var bodyClassString = classNames({
    'vra-modal-body': true,
    'vra-modal-content': true
  });

  const showsSeasonsSelectedLayer = showsSeasons && selectedLayer;
  const showsDatesSelectedSeason = showsDates && selectedSeason;

  return (
    <Modal
      className={classString}
      onClose={onCancelModal}
      show={true}
      size='XL'
    >
      <header className='vra-modal-header'>
        {getMessage('title-text__layer-select')}
      </header>
      <section className={bodyClassString}>
        <table className='vra-layer-selector__table'>
          <thead>
            <tr>
              <th>{getMessage('label-text__layer-select')}</th>
              <th/>
              <th className='vra-layer-selector-th--seasons'>{
                showsSeasonsSelectedLayer && getMessage('label-text__season')
              }</th>
              <th>{showsDatesSelectedSeason && getMessage('label-text__image-date')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <OverlaySelectLayerGroups
                  groups={layerGroups}
                  onSelect={onSelectLayerGroup}
                  selectedGroup={selectedLayerGroup}
                />
              </td>
              <td>{
                selectedLayerGroup
                  && <OverlaySelectLayers
                    layerGroup={selectedLayerGroup}
                    onSelect={onSelectLayer}
                    selectedLayer={selectedLayer}
                  />
              }</td>
              <td>{
                showsSeasonsSelectedLayer
                  && <OverlaySelectLayerSeasons
                    onSelect={onSelectSeason}
                    seasons={layerSeasons}
                    selectedSeason={selectedSeason}
                  />
              }</td>
              <td>{
                showsDatesSelectedSeason
                  && <OverlaySelectLayerDates
                    datesAndImages={layerImages}
                    onSelect={onSelectUuid}
                    selectedUuid={selectedUuid}
                  />
              }</td>
            </tr>
          </tbody>
        </table>
      </section>
      <footer className='vra-modal-footer'>
        {uploadTrigger}
        <Button
          className='vra-layer-selector__button--cancel'
          data-test-id={`${baseString}-button-0`}
          mode='secondary'
          onClick={onCancelModal}>
          {getMessage('button-text__cancel')}
        </Button>
        <Button
          className={'vra-layer-selector__button--done'}
          data-test-id={`${baseString}-button-1`}
          disabled={!selectedLayer}
          mode='primary'
          onClick={ () => onSaveModal({ layer: selectedLayer, uuid: selectedUuid }) }>
          {getMessage('button-text__done')}
        </Button>
      </footer>
    </Modal>
  );
};
OverlaySelectModalContainer.propTypes = {
  layerGroups: propTypes.array,
  layerImages: propTypes.array,
  layerSeasons: propTypes.array,
  onCancelModal: propTypes.func.isRequired,
  onSaveModal: propTypes.func.isRequired,
  onSelectLayer: propTypes.func.isRequired,
  onSelectLayerGroup: propTypes.func.isRequired,
  onSelectSeason: propTypes.func.isRequired,
  onSelectUuid: propTypes.func.isRequired,
  selectedLayer: propTypes.string,
  selectedLayerGroup: propTypes.object,
  selectedSeason: propTypes.string,
  selectedUuid: propTypes.string,
  showsDates: propTypes.bool.isRequired,
  showsSeasons: propTypes.bool.isRequired
};

OverlaySelectModalContainer = connect(
  (state) => {
    const fieldId = getActiveFieldId(state);

    const selectedLayerGroup = getSelectedOverlayLayerGroup(state);
    const layerGroups = getUsableOverlayLayerGroups(state);
    const selectedLayer = getSelectedOverlayLayer(state);
    const selectedSeason = getSelectedOverlaySeason(state);
    const selectedUuid = getSelectedOverlayUuid(state);
    const showsDates = showsOverlayDates(state);
    const showsSeasons = showsOverlaySeasons(state);
    let layerSeasons;
    let layerImages;

    if(showsSeasons && selectedLayer && selectedLayerGroup) {
      layerSeasons = getImageSeasonsOfLayerForField({ state, layer: selectedLayer, fieldId });
    }
    if(showsDates && selectedSeason) {
      layerImages = getImagesOfLayerForField({ state, layer: selectedLayer, fieldId, season: selectedSeason });
    }

    return {
      layerGroups,
      layerImages,
      layerSeasons,
      selectedLayer,
      selectedLayerGroup,
      selectedSeason,
      selectedUuid,
      showsDates,
      showsSeasons
    };
  },
  (dispatch) => {
    return {
      onCancelModal: () => dispatch(setIsOverlaySelectModalActive(false)),
      onSaveModal: ({ uuid, layer }) => {
        dispatch( setIsOverlaySelectModalActive(false) );
        dispatch( setActiveOverlayInfo( { uuid, layer }) );
      },
      onSelectLayerGroup: (group) => dispatch(setSelectedOverlayLayerGroup(group)),
      onSelectLayer: (layer) => dispatch(setSelectedOverlayLayerWithDefaults(layer)),
      onSelectSeason: (season) => dispatch(setSelectedOverlaySeasonWithDefaults({season})),
      onSelectUuid: (uuid) => dispatch(setSelectedOverlayUuid(uuid))
    };
  }
)(OverlaySelectModalContainer);

export default OverlaySelectModalContainer;
